import * as turf from '@turf/turf';
import type { Feature, Geometry, MultiPolygon, Polygon } from 'geojson';

export const ONBOARDING_PARAM = 'onboarding';
export const ONBOARDING_HREF = `/hub?${ONBOARDING_PARAM}=1`;

/** Steps whose completion comes from real org data. */
export type OnboardingDataStep = 'aors' | 'assets' | 'rules';
export type OnboardingStepId = OnboardingDataStep | 'finish';

export const ONBOARDING_DATA_STEPS: readonly OnboardingDataStep[] = [
  'aors',
  'assets',
  'rules',
];
export const ONBOARDING_STEPS: readonly OnboardingStepId[] = [
  ...ONBOARDING_DATA_STEPS,
  'finish',
];

export type OrgOnboardingState = {
  group_guid: string;
  skipped_steps: OnboardingDataStep[];
  started_at: string | null;
  started_by: string | null;
  completed_at: string | null;
  completed_by: string | null;
  updated_at: string | null;
};

export type OnboardingAction =
  | { action: 'start' }
  | { action: 'complete' }
  | { action: 'restart' }
  | { action: 'skip'; step: OnboardingDataStep }
  | { action: 'unskip'; step: OnboardingDataStep };

export type OnboardingCounts = Record<OnboardingDataStep, number>;

export type OnboardingStepStatus = 'done' | 'skipped' | 'todo';

export function stepStatus(
  step: OnboardingDataStep,
  counts: OnboardingCounts,
  skipped: readonly OnboardingDataStep[]
): OnboardingStepStatus {
  if (counts[step] > 0) return 'done';
  if (skipped.includes(step)) return 'skipped';
  return 'todo';
}

export type OnboardingProgress = {
  /** Steps done or skipped. */
  settled: number;
  total: number;
  /** E.g. "2 of 3". */
  label: string;
  allSettled: boolean;
  /** First step still to do, or `finish` when every step is settled. */
  nextStep: OnboardingStepId;
};

export function onboardingProgress(
  counts: OnboardingCounts,
  skipped: readonly OnboardingDataStep[]
): OnboardingProgress {
  const statuses = ONBOARDING_DATA_STEPS.map((step) =>
    stepStatus(step, counts, skipped)
  );
  const settled = statuses.filter((status) => status !== 'todo').length;
  const total = ONBOARDING_DATA_STEPS.length;
  const firstTodo = ONBOARDING_DATA_STEPS.find(
    (_step, index) => statuses[index] === 'todo'
  );
  return {
    settled,
    total,
    label: `${settled} of ${total}`,
    allSettled: settled === total,
    nextStep: firstTodo ?? 'finish',
  };
}

export function isOnboardingStepId(
  value: string | null
): value is OnboardingStepId {
  return ONBOARDING_STEPS.includes(value as OnboardingStepId);
}

/**
 * The one-time setup banner: admins of an org that has no AORs and has never
 * opened organization setup.
 */
export function shouldShowSetupBanner(params: {
  isAdmin: boolean;
  aorCount: number | null;
  state: OrgOnboardingState | null | undefined;
  dismissed: boolean;
}): boolean {
  if (!params.isAdmin || params.dismissed) return false;
  if (params.aorCount == null || params.aorCount > 0) return false;
  if (!params.state) return false;
  return !params.state.started_at && !params.state.completed_at;
}

type AorWithGeometry = {
  id: string;
  name: string;
  geolocation?: {
    geojson?: { data?: { features?: Feature[] } };
  } | null;
};

type AssetWithLocation = {
  id: string;
  geolocation?: {
    centroid?: Feature | null;
    geojson?: { data?: { features?: Feature[] } };
  } | null;
};

function areaOf(aor: AorWithGeometry): Polygon | MultiPolygon | null {
  const geometry = aor.geolocation?.geojson?.data?.features?.[0]?.geometry as
    | Geometry
    | undefined;
  if (geometry?.type === 'Polygon' || geometry?.type === 'MultiPolygon') {
    return geometry;
  }
  return null;
}

function pointOf(asset: AssetWithLocation): [number, number] | null {
  const candidates = [
    asset.geolocation?.centroid?.geometry,
    asset.geolocation?.geojson?.data?.features?.[0]?.geometry,
  ];
  for (const geometry of candidates) {
    if (geometry?.type === 'Point') {
      const [lng, lat] = geometry.coordinates;
      if (Number.isFinite(lng) && Number.isFinite(lat)) return [lng, lat];
    }
  }
  return null;
}

export type AorAssetCount = { aorId: string; name: string; count: number };

/**
 * Assets inside each polygon AOR, plus assets with no location, so an admin can
 * spot geocoding misses before rules depend on them.
 */
export function assetCountsByAor(
  aors: readonly AorWithGeometry[],
  assets: readonly AssetWithLocation[]
): { perAor: AorAssetCount[]; unlocated: number; outside: number } {
  const areas = aors
    .map((aor) => ({ aor, area: areaOf(aor) }))
    .filter(
      (
        entry
      ): entry is { aor: AorWithGeometry; area: Polygon | MultiPolygon } =>
        entry.area !== null
    );
  const perAor = areas.map(({ aor }) => ({
    aorId: aor.id,
    name: aor.name,
    count: 0,
  }));
  let unlocated = 0;
  let outside = 0;
  for (const asset of assets) {
    const point = pointOf(asset);
    if (!point) {
      unlocated += 1;
      continue;
    }
    let inside = false;
    areas.forEach(({ area }, index) => {
      if (turf.booleanPointInPolygon(point, area)) {
        perAor[index].count += 1;
        inside = true;
      }
    });
    if (!inside) outside += 1;
  }
  return { perAor, unlocated, outside };
}

/** What "Ask PRATUS AI" sends for each step. */
export function askAiPrompt(step: OnboardingDataStep, aorTerm: string): string {
  if (step === 'aors') {
    return `Help me set up our ${aorTerm}. List the ones we already have and suggest what areas we should add.`;
  }
  if (step === 'assets') {
    return 'Help me import our facilities and assets. I will paste or attach the list.';
  }
  return `Draft event automation rules for our ${aorTerm} using the analytics-ready hazard feeds (hurricanes, wildfires, severe storms, earthquakes).`;
}
